/* @jsx jsx */
import { css, jsx } from '@emotion/react';
import type { Interpolation } from '@emotion/react';

import * as React from 'react';

import { Grid, breakpoints } from '@coursera/cds-core';
import type { Theme } from '@coursera/cds-core';
import {
  CertificateIcon,
  DegreeIcon,
  EditIcon,
  GlobeIcon,
  HonorsAssignmentIcon,
  InstitutionIcon,
  LaptopIcon,
  StopwatchIcon,
  TrophyIcon,
} from '@coursera/cds-icons';

import Markdown from 'bundles/cms/components/Markdown';
import type { Maybe } from 'bundles/premium-certificate/__generated__/baseContentfulTypes';
import type { CertificateOverviewFragment } from 'bundles/premium-certificate/__generated__/contentfulTypes';
import Container from 'bundles/xddp/components/shared/Container';

import _t from 'i18n!nls/premium-certificate';

type Props = CertificateOverviewFragment;

const ICONS = {
  Trophy: TrophyIcon,
  'Graduation Cap': DegreeIcon,
  Stopwatch: StopwatchIcon,
  Certificate: CertificateIcon,
  credit: CertificateIcon,
  Institution: InstitutionIcon,
  Pencil: EditIcon,
  Laptop: LaptopIcon,
  HonorsAssignment: HonorsAssignmentIcon,
  Globe: GlobeIcon,
};

export type IconKeys = keyof typeof ICONS;

function isIconType(key?: string | null): key is IconKeys {
  return (key && key in ICONS) || false;
}

const styles: Record<string, Interpolation<Theme>> = {
  overviewVisualCol: () => css`
    padding: 0 24px;
    display: flex;
    justify-content: center;
    ${breakpoints.down('sm')} {
      display: none;
    }
  `,
  overviewContainer: () => css`
    ${breakpoints.down('sm')} {
      padding: 0;
    }
  `,
  certificateOverviewWrapper: () => css`
    margin: 3.75rem auto;
    ${breakpoints.down('sm')} {
      margin: 3rem 1.5rem;
    }
  `,
  overviewTitle: () => css`
    font-family: inherit;
    color: var(--cds-color-grey-975);
    font-size: 1.5rem;
    line-height: 1.875rem;
    font-weight: 700;
    margin-bottom: 3rem;
    text-align: center;
    ${breakpoints.down('sm')} {
      text-align: left;
      margin-bottom: 1.875rem;
    }
  `,
  overviewVisualContainer: css`
    height: 100%;
    max-height: 432px;
  `,
  overviewVisual: css`
    max-height: 100%;
    max-width: 100%;
  `,
  valuePropContainer: () => css`
    display: flex;
    margin-bottom: 1.5rem;
    ${breakpoints.down('sm')} {
      padding: 0;
    }
  `,
  valuePropIcon: () => css`
    height: 40px;
    width: 40px;
    border: 2px solid var(--cds-color-grey-50);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  `,
  valuePropDescription: () => css`
    margin-left: 1.5rem;
    ${breakpoints.down('sm')} {
      margin-left: 1rem;
    }

    .rc-Markdown.styled {
      h3 {
        color: var(--cds-color-grey-975);
        font-size: 1.25rem;
        line-height: 1.5rem;
        font-weight: 700;
      }
    }
  `,
};

export const OverviewValueProp = ({ icon, description }: { description?: Maybe<string>; icon?: Maybe<IconKeys> }) => {
  if (!description || !icon) {
    return null;
  }

  const SvgIcon = icon && ICONS[icon];

  return (
    <div data-test="OverviewValueProp" css={styles.valuePropContainer}>
      <div css={styles.valuePropIcon}>
        <SvgIcon size="medium" />
      </div>
      <div css={styles.valuePropDescription}>
        <Markdown source={description} />
      </div>
    </div>
  );
};

const CertificateOverview = ({
  overviewTitle,
  overviewValuePropOne,
  overviewValuePropOneIcon,
  overviewValuePropTwo,
  overviewValuePropTwoIcon,
  overviewValuePropThree,
  overviewValuePropThreeIcon,
  overviewVisual,
}: Props) => {
  const noValuePropsDefined = !overviewValuePropOne && !overviewValuePropTwo && !overviewValuePropThree;

  if (noValuePropsDefined) {
    return null;
  }

  const iconOne = isIconType(overviewValuePropOneIcon) ? overviewValuePropOneIcon : undefined;
  const iconTwo = isIconType(overviewValuePropTwoIcon) ? overviewValuePropTwoIcon : undefined;
  const iconThree = isIconType(overviewValuePropThreeIcon) ? overviewValuePropThreeIcon : undefined;

  return (
    <div data-test="CertificateOverview" css={styles.certificateOverviewWrapper}>
      <Container css={styles.overviewContainer}>
        <Grid container justifyContent="center">
          <Grid item>
            <h2 css={styles.overviewTitle}>{overviewTitle || _t('No application necessary—enroll today')}</h2>
          </Grid>
        </Grid>
        <Grid container>
          <Grid item md={6}>
            <OverviewValueProp description={overviewValuePropOne} icon={iconOne} />
            <OverviewValueProp description={overviewValuePropTwo} icon={iconTwo} />
            <OverviewValueProp description={overviewValuePropThree} icon={iconThree} />
          </Grid>
          <Grid item md={6} css={styles.overviewVisualCol}>
            {overviewVisual?.url && (
              <div aria-hidden css={styles.overviewVisualContainer} data-test="overview-visual">
                <img
                  css={styles.overviewVisual}
                  src={overviewVisual.url}
                  alt={overviewVisual.title || _t('Certificate Overview Visual')}
                />
              </div>
            )}
          </Grid>
        </Grid>
      </Container>
    </div>
  );
};

export default CertificateOverview;
